import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, User, Mail, Calendar, Trash2, 
  AlertTriangle, Loader2, Shield, Bell, LogOut, Download, FileText,
  CheckCircle2, Fingerprint
} from 'lucide-react';
import { toast } from 'sonner';
import { useBiometrics } from '../hooks/useBiometrics';
import PushNotificationToggle from '../components/PushNotificationToggle';
import TimezoneSetting from '../components/TimezoneSetting';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, logout, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonOther, setDeleteReasonOther] = useState('');
  const [stats, setStats] = useState(null);
  const [exportingData, setExportingData] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({
    reminder_emails: true,
    retake_reminders: true,
    product_updates: false,
    weekly_wellness_report: true,
    wrap_up_nudge: true
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const { isAvailable: bioAvailable, isEnabled: bioEnabled, isLoading: bioLoading, enableBiometrics, disableBiometrics } = useBiometrics();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/profile');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;
      try {
        const res = await axios.get(`${API}/user/stats`, { headers: getAuthHeader() });
        setStats(res.data);
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };
    
    const fetchEmailPrefs = async () => {
      if (!user) return;
      try {
        const res = await axios.get(`${API}/user/email-preferences`, { headers: getAuthHeader() });
        if (res.data) {
          setEmailPrefs(res.data);
        }
      } catch (error) {
        console.error('Error fetching email preferences:', error);
      }
    };
    
    fetchStats();
    fetchEmailPrefs();
  }, [user, getAuthHeader]);

  const handleSaveEmailPrefs = async () => {
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/user/email-preferences`, emailPrefs, { headers: getAuthHeader() });
      toast.success('Email preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    const reason = deleteReason === 'Other' ? deleteReasonOther.trim() : deleteReason;
    if (!reason) {
      toast.error('Please select a reason for deleting your account');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/user/delete-account`, { 
        headers: getAuthHeader(),
        data: { reason }
      });
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await axios.get(`${API}/user/export-data`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vitala_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExportingData(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-8">Profile Settings</h1>

          {/* User Info Card */}
          <div className="bg-white rounded-2xl border border-border p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Account Information
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>
              
              {user?.name && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{user.name}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Stats */}
          {stats && (
            <div className="bg-white rounded-2xl border border-border p-6 mb-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Your Data
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-primary">{stats.assessments || 0}</p>
                  <p className="text-sm text-muted-foreground">Assessments</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-primary">{stats.journal_entries || 0}</p>
                  <p className="text-sm text-muted-foreground">Journal Entries</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-primary">{stats.symptom_logs || 0}</p>
                  <p className="text-sm text-muted-foreground">Symptom Logs</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-primary">{stats.reminders || 0}</p>
                  <p className="text-sm text-muted-foreground">Reminders</p>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          <div className="bg-white rounded-2xl border border-border p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4">Push Notifications</h2>
            <PushNotificationToggle />
          </div>

          {/* Timezone */}
          <TimezoneSetting />

          {/* Quick Links */}
          <div className="bg-white rounded-2xl border border-border p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/reminders')}
              >
                <Bell className="w-4 h-4 mr-2" />
                Manage Reminders
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/forgot-password')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/privacy')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Privacy Policy
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-muted-foreground"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {/* Email Preferences */}
          <div className="bg-white rounded-2xl border border-border p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email Preferences
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Manage what emails you receive from Vitala.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Supplement Reminders</p>
                  <p className="text-xs text-muted-foreground">Daily reminders to take your supplements</p>
                </div>
                <Switch 
                  checked={emailPrefs.reminder_emails}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, reminder_emails: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Assessment Retake Reminders</p>
                  <p className="text-xs text-muted-foreground">Reminders to retake your health assessment every 3 months</p>
                </div>
                <Switch 
                  checked={emailPrefs.retake_reminders}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, retake_reminders: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Product Updates</p>
                  <p className="text-xs text-muted-foreground">News about new features and improvements</p>
                </div>
                <Switch 
                  checked={emailPrefs.product_updates}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, product_updates: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Weekly Wellness Report</p>
                  <p className="text-xs text-muted-foreground">Personalized summary of your streak, wellness trends, and restock reminders every Monday</p>
                </div>
                <Switch 
                  checked={emailPrefs.weekly_wellness_report}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, weekly_wellness_report: checked }))}
                  data-testid="weekly-wellness-report-toggle"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Nightly Wrap-Up Nudge</p>
                  <p className="text-xs text-muted-foreground">A gentle push reminder ~60 min after your last reminder to log what you took and how you&apos;re feeling</p>
                </div>
                <Switch
                  checked={emailPrefs.wrap_up_nudge}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, wrap_up_nudge: checked }))}
                  data-testid="wrap-up-nudge-toggle"
                />
              </div>
              
              <Button 
                onClick={handleSaveEmailPrefs}
                disabled={savingPrefs}
                className="w-full"
              >
                {savingPrefs ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Save Preferences
              </Button>
            </div>
          </div>

          {/* Biometric Login Section */}
          {bioAvailable && (
            <div className="bg-white rounded-2xl border border-border p-6 mb-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-primary" />
                Biometric Login
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Use Face ID or Touch ID for quick, secure sign-in without entering your password.
              </p>

              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <p className="font-medium text-sm">Face ID / Touch ID</p>
                  <p className="text-xs text-muted-foreground">
                    {bioEnabled ? 'Enabled for quick sign-in' : 'Enable biometric authentication'}
                  </p>
                </div>
                <Switch
                  checked={bioEnabled}
                  disabled={bioLoading}
                  data-testid="biometric-toggle"
                  onCheckedChange={async (checked) => {
                    if (checked) {
                      const result = await enableBiometrics(user?.id, user?.name || user?.email);
                      if (result.success) {
                        toast.success('Biometric login enabled!');
                      } else {
                        toast.error('Failed to enable biometrics. Please try again.');
                      }
                    } else {
                      disableBiometrics();
                      toast.info('Biometric login disabled');
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Data Privacy Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6">
            <h2 className="font-semibold text-lg mb-2 flex items-center gap-2 text-blue-700">
              <Shield className="w-5 h-5" />
              Your Data Rights
            </h2>
            <p className="text-sm text-blue-600 mb-4">
              Under GDPR and similar regulations, you have the right to access and export all your data, 
              or request its deletion.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline"
                onClick={handleExportData}
                disabled={exportingData}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="export-data-btn"
              >
                {exportingData ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportingData ? 'Exporting...' : 'Export My Data'}
              </Button>
              <p className="text-xs text-blue-600 self-center">
                Download all your data in JSON format
              </p>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <h2 className="font-semibold text-lg mb-2 flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </h2>
            <p className="text-sm text-red-600 mb-4">
              Deleting your account will permanently remove all your data including assessments, 
              journal entries, symptom logs, and reminders. This action cannot be undone.
            </p>
            
            {!showDeleteConfirm ? (
              <Button 
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">
                    Why are you leaving? <span className="text-red-500">*</span>
                  </p>
                  <div className="space-y-2">
                    {[
                      'Not seeing results',
                      'Too expensive',
                      'Found an alternative',
                      'Privacy concerns',
                      'Too complicated to use',
                      'Other'
                    ].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          deleteReason === option
                            ? 'border-red-400 bg-red-50'
                            : 'border-border hover:border-red-200'
                        }`}
                        data-testid={`delete-reason-${option.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <input
                          type="radio"
                          name="deleteReason"
                          value={option}
                          checked={deleteReason === option}
                          onChange={(e) => setDeleteReason(e.target.value)}
                          className="accent-red-600"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                  {deleteReason === 'Other' && (
                    <Input
                      value={deleteReasonOther}
                      onChange={(e) => setDeleteReasonOther(e.target.value)}
                      placeholder="Please tell us why..."
                      className="mt-2 border-red-200"
                      data-testid="delete-reason-other-input"
                    />
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium text-red-700 mb-1">
                    Type <span className="font-bold">DELETE</span> to confirm:
                  </p>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    className="border-red-300"
                    data-testid="delete-confirm-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={isLoading || deleteConfirmText !== 'DELETE' || !deleteReason || (deleteReason === 'Other' && !deleteReasonOther.trim())}
                    data-testid="confirm-delete-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Permanently Delete Account'
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setDeleteReason('');
                      setDeleteReasonOther('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
